import { useMemo, useState } from 'react';
import { Search, Check, ChevronDown, Users } from 'lucide-react';
import type { ConsultorElegivel } from '@/hooks/useConsultoresElegiveis';

/**
 * O seletor de "para quem vai a carteira" — o MESMO em todas as telas que
 * trocam dono (Carteira, Carteira Claro, Busca 360, Gestão de Leads, Meus
 * Leads). Antes eram quatro widgets diferentes para a mesma pergunta: combobox,
 * select do shadcn, lista de checkbox artesanal e `<select>` nativo.
 *
 * O que ele mostra, e por quê:
 * - FOTO real (`usuarios.foto_url`) com `object-cover` — sem isso o rosto
 *   distorce; iniciais são só o fallback de quem não tem foto
 * - cargo POR EXTENSO e equipe — chave crua (`CONSULTOR_JUNIOR`) não vai à tela
 * - **N clientes**: é o dado que decide para quem passar a carteira
 */

const iniciais = (nome: string) =>
  (nome || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

export function FotoConsultor({ url, nome, size = 32, realce = false }: {
  url: string | null; nome: string; size?: number; realce?: boolean;
}) {
  const conteudo = url ? (
    <img
      src={url}
      alt={nome}
      className="h-full w-full rounded-full object-cover"
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
    />
  ) : (
    <span className="font-bold text-[#6B5A4D] dark:text-[#B5A8A8]" style={{ fontSize: Math.round(size * 0.34) }}>
      {iniciais(nome)}
    </span>
  );
  return (
    <span
      className="grid flex-shrink-0 place-items-center overflow-hidden rounded-full bg-[#F1EDE7] dark:bg-[#2A1F1F]"
      style={{
        width: size,
        height: size,
        ...(realce ? { boxShadow: '0 0 0 2px #5B0A0D' } : {}),
      }}
    >
      {conteudo}
    </span>
  );
}

export function SeletorConsultor({
  consultores,
  valor,
  valores,
  onEscolher,
  onAlternar,
  carregando,
  placeholder = 'Escolha o consultor',
  alturaLista = 220,
}: {
  consultores: ConsultorElegivel[];
  valor?: string | null;
  valores?: string[];
  onEscolher?: (id: string) => void;
  onAlternar?: (id: string) => void;
  carregando?: boolean;
  placeholder?: string;
  alturaLista?: number;
}) {
  const [busca, setBusca] = useState('');
  const multiplos = Array.isArray(valores);
  const valoresAtivos = valores ?? [];
  const escolhido = consultores.find((c) => c.id === valor) || null;
  const escolhidos = multiplos ? consultores.filter((c) => valoresAtivos.includes(c.id)) : [];

  // Busca por nome, equipe OU cargo — os três jeitos de procurar alguém.
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return consultores;
    return consultores.filter((c) =>
      c.nome.toLowerCase().includes(q)
      || (c.equipeNome || '').toLowerCase().includes(q)
      || c.cargo.toLowerCase().includes(q));
  }, [consultores, busca]);

  return (
    <div className="overflow-hidden rounded-xl border-[1.5px] border-[#5B0A0D]/45 shadow-[0_0_0_3px_rgba(91,10,13,0.07)] dark:border-[#9D2530]/50">
      {/* O escolhido, sempre visível no topo do campo */}
      <div className="flex h-[46px] items-center gap-2.5 bg-[#FAF8F5] px-3 dark:bg-white/[0.04]">
        {multiplos && escolhidos.length > 0 ? (
          <>
            <span className="grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-full bg-muted">
              <Users className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-bold text-foreground">
                {escolhidos.length} consultores selecionados
              </span>
              <span className="block truncate text-[10px] text-muted-foreground">
                {escolhidos.map((consultor) => consultor.nome).join(' · ')}
              </span>
            </span>
          </>
        ) : escolhido ? (
          <>
            <FotoConsultor url={escolhido.foto_url} nome={escolhido.nome} size={30} realce />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-bold text-[#2E2019] dark:text-[#E8DEDE]">
                {escolhido.nome}
              </span>
              <span className="block truncate text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                {escolhido.cargo}
                {escolhido.equipeNome ? ` · ${escolhido.equipeNome}` : ''} ·{' '}
                <span className="tabular-nums">{escolhido.qtdClientes}</span> clientes
              </span>
            </span>
          </>
        ) : (
          <>
            <span className="grid h-[30px] w-[30px] flex-shrink-0 place-items-center rounded-full bg-[#F1EDE7] dark:bg-[#2A1F1F]">
              <Users className="h-3.5 w-3.5 text-[#6B5A4D] dark:text-[#B5A8A8]" />
            </span>
            <span className="flex-1 text-[12.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">{placeholder}</span>
          </>
        )}
        <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-[#6B5A4D]/60 dark:text-[#B5A8A8]/60" />
      </div>

      {/* Busca */}
      <div className="flex h-[34px] items-center gap-2 border-y border-[#E7DCCB] bg-white px-3 dark:border-[#3A2A2A] dark:bg-[#231A1A]">
        <Search className="h-3.5 w-3.5 flex-shrink-0 text-[#6B5A4D]/60 dark:text-[#B5A8A8]/60" />
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="buscar por nome, equipe ou cargo"
          className="min-w-0 flex-1 bg-transparent text-[11.5px] text-[#2E2019] outline-none placeholder:text-[#6B5A4D]/60 dark:text-[#E8DEDE] dark:placeholder:text-[#B5A8A8]/60"
        />
        <span className="flex-shrink-0 text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
          <span className="tabular-nums">{filtrados.length}</span> ativos
        </span>
      </div>

      {/* A lista, aberta dentro do próprio campo */}
      <div className="overflow-y-auto bg-white dark:bg-[#231A1A]" style={{ maxHeight: alturaLista }}>
        {carregando ? (
          <div className="flex h-20 items-center justify-center text-[11.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">
            Carregando consultores…
          </div>
        ) : filtrados.length === 0 ? (
          <div className="flex h-20 items-center justify-center text-[11.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">
            Ninguém encontrado com esse termo.
          </div>
        ) : (
          filtrados.map((c) => {
            const ativo = multiplos ? valoresAtivos.includes(c.id) : c.id === valor;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => multiplos ? onAlternar?.(c.id) : onEscolher?.(c.id)}
                aria-pressed={ativo}
                className={`flex h-[44px] w-full items-center gap-2.5 border-b border-[#EFE9E0] px-3 text-left transition-colors last:border-b-0 dark:border-[#3A2A2A] ${
                  ativo ? 'bg-[#F6EFEF] dark:bg-[#332727]' : 'hover:bg-[#FBF9F6] dark:hover:bg-white/[0.03]'
                }`}
              >
                <FotoConsultor url={c.foto_url} nome={c.nome} size={30} realce={ativo} />
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-[12.5px] text-[#2E2019] dark:text-[#E8DEDE] ${ativo ? 'font-bold' : 'font-semibold'}`}>
                    {c.nome}
                  </span>
                  <span className="block truncate text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                    {c.cargo}{c.equipeNome ? ` · ${c.equipeNome}` : ''}
                  </span>
                </span>
                <span className="flex-shrink-0 text-[10.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                  <span className="font-bold tabular-nums text-[#2E2019] dark:text-[#E8DEDE]">{c.qtdClientes}</span> clientes
                </span>
                {ativo && <Check className="h-3.5 w-3.5 flex-shrink-0 text-[#5B0A0D] dark:text-[#C87484]" />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
